import { useState } from 'react';
import styled from '@emotion/styled'
import { ToggleButtonGroup, ToggleButton, Tooltip, useTheme } from '@mui/material';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateApptStatus } from '../apiService';

import useSnackbarStore from '../../../store/snackbar';

import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import PersonOffRoundedIcon from '@mui/icons-material/PersonOffRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';

const RaisedTooltip = styled(Tooltip)`
  z-index: ${props => props.$raised ? "6100" : 100};
`;

const Text = styled.div`
  font-size: 1.2rem;
  min-width: fit-content;
`;

const AppointmentMenu = ({ event = {}, displayText = false, raised = true, bgColor }) => {
  // Optimistic override - cleared once server confirms via event prop update
  const [optimisticStatus, setOptimisticStatus] = useState(null);

  const queryClient = useQueryClient();
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);
  const theme = useTheme();

  const { mutate: updateApptMutation } = useMutation({
    mutationFn: updateApptStatus,
    onMutate: async ({ status: newStatus }) => {
      setOptimisticStatus(newStatus);
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['appointments', 'calendar'] });
      await queryClient.cancelQueries({ queryKey: ['appointments'] });
    },
    onSuccess: () => {
      showSnackbar('Updated successfully!', 'success');
    },
    onError: () => {
      showSnackbar('Failed to update!', 'error');
      setOptimisticStatus(null); // revert on error
    },
    onSettled: () => {
      // Always refetch after every mutation settles (success or error)
      queryClient.invalidateQueries({ queryKey: ['appointments', 'calendar'] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      setOptimisticStatus(null); // let the fresh server data take over
    }
  });

  // Derive displayed status from props, with optimistic override
  const status = optimisticStatus ?? event.status ?? "scheduled";

  const handleStatusChange = (e, newStatus) => {
    e.stopPropagation();
    if (!newStatus || newStatus === status) return; // ToggleButtonGroup can fire null on re-click
    updateApptMutation({ id: event.appointment_id, status: newStatus });
  };

  let statuses = [
    {
      value: "scheduled",
      label: "Scheduled",
      icon: (size) => <PendingActionsRoundedIcon fontSize={size ? size : "medium"} />
    },
    {
      value: "completed",
      label: "Show",
      icon: (size) => <HowToRegRoundedIcon fontSize={size ? size : "medium"} />
    },
    {
      value: "no_show",
      label: "No Show",
      icon: (size) => <PersonOffRoundedIcon fontSize={size ? size : "medium"} />
    },
    {
      value: "cancelled",
      label: "Canceled",
      icon: (size) => <EventBusyRoundedIcon fontSize={size ? size : "medium"} />
    }
  ];

  if (status === "cancelled") {
    statuses = statuses.filter(s => ["completed", "no_show", "cancelled"].includes(s.value));
  }

  if (event.invoice_status) {
    statuses = statuses.filter(s => ["completed", "no_show"].includes(s.value));
  }

  return (
    <ToggleButtonGroup
      color="primary"
      value={status}
      exclusive
      onChange={handleStatusChange}
      size="small"
      sx={{ backgroundColor: bgColor || theme.palette.background.default }}
    >
      {statuses.map((s) => (
        <RaisedTooltip key={s.value} title={s.label} $raised={raised}>
          <ToggleButton value={s.value} sx={{ gap: '.3rem', minWidth: "fit-content" }}>
            {s.icon(displayText && "small")} {displayText && <Text>{s.label}</Text>}
          </ToggleButton>
        </RaisedTooltip>
      ))}
    </ToggleButtonGroup>
  );
};

export default AppointmentMenu;